import { create } from 'zustand'
import { getSunTimes } from '../lib/sun'

export type MapStyleId = 'standard' | 'lineart' | 'satellite'

export type SavedSpot = {
  id: string
  name: string
  lng: number
  lat: number
  createdAt: string
}

export type FrameVector = {
  bearing: number
  active: boolean
} | null

export type PresetKind =
  | 'sunrise'
  | 'goldenMorning'
  | 'solarNoon'
  | 'goldenEvening'
  | 'sunset'
  | 'blueEvening'

type AppState = {
  dateTime: Date
  pin: { lng: number; lat: number } | null
  mapStyle: MapStyleId
  pitchEnabled: boolean
  playing: boolean
  frame: FrameVector
  savedSpots: SavedSpot[]
  searchQuery: string
  illuminated: boolean | null
  buildingsLoading: boolean
  setDateTime: (d: Date) => void
  setPin: (p: { lng: number; lat: number } | null) => void
  setMapStyle: (s: MapStyleId) => void
  setPitchEnabled: (v: boolean) => void
  setPlaying: (v: boolean) => void
  setFrame: (f: FrameVector) => void
  setSearchQuery: (q: string) => void
  setIlluminated: (v: boolean | null) => void
  setBuildingsLoading: (v: boolean) => void
  addSavedSpot: (spot: Omit<SavedSpot, 'id' | 'createdAt'>) => boolean
  removeSavedSpot: (id: string) => void
  jumpToPreset: (kind: PresetKind) => void
}

const STORAGE_KEY = 'sunscout-spots-v1'
const FREE_SPOT_LIMIT = 3

function loadSpots(): SavedSpot[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    return JSON.parse(raw) as SavedSpot[]
  } catch {
    return []
  }
}

function persistSpots(spots: SavedSpot[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(spots))
}

export const useAppStore = create<AppState>((set, get) => ({
  dateTime: new Date(),
  pin: { lng: 17.0385, lat: 51.1079 },
  mapStyle: 'standard',
  pitchEnabled: true,
  playing: false,
  frame: null,
  savedSpots: typeof window !== 'undefined' ? loadSpots() : [],
  searchQuery: '',
  illuminated: null,
  buildingsLoading: false,

  setDateTime: (d) => set({ dateTime: new Date(d.getTime()) }),
  setPin: (p) => set({ pin: p }),
  setMapStyle: (s) => set({ mapStyle: s }),
  setPitchEnabled: (v) => set({ pitchEnabled: v }),
  setPlaying: (v) => set({ playing: v }),
  setFrame: (f) => set({ frame: f }),
  setSearchQuery: (q) => set({ searchQuery: q }),
  setIlluminated: (v) => set({ illuminated: v }),
  setBuildingsLoading: (v) => set({ buildingsLoading: v }),

  addSavedSpot: (spot) => {
    const current = get().savedSpots
    if (current.length >= FREE_SPOT_LIMIT) return false
    const next: SavedSpot[] = [
      ...current,
      {
        ...spot,
        id: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
      },
    ]
    persistSpots(next)
    set({ savedSpots: next })
    return true
  },

  removeSavedSpot: (id) => {
    const next = get().savedSpots.filter((s) => s.id !== id)
    persistSpots(next)
    set({ savedSpots: next })
  },

  jumpToPreset: (kind) => {
    const pin = get().pin
    if (!pin) return
    const base = get().dateTime
    const times = getSunTimes(pin.lat, pin.lng, base)
    const map: Record<PresetKind, Date | null> = {
      sunrise: times.sunrise,
      goldenMorning: times.goldenHourEnd,
      solarNoon: times.solarNoon,
      goldenEvening: times.goldenHour,
      sunset: times.sunset,
      blueEvening: times.dusk,
    }
    const target = map[kind]
    if (target) set({ dateTime: target, playing: false })
  },
}))
